import React, { useEffect } from "react";
import { GiftCard, GiftCardPurchaseOption } from "../../types/gift-card";
import styled, { css } from "styled-components";
import { breakpoints } from "../../utils/styles";

const List = styled.div<Pick<Props, "animated">>`
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: center;
    gap: 0.5rem;
    margin: auto;

    @media screen and (min-width: ${breakpoints.desktop}px) {
        display: block;
        max-width: none;
    }

    ${({ animated }) => animated && css`
        gap: 0;
    `}
`;

const Card = styled.div<Pick<Props, "animated">>`
    position: relative;
    cursor: pointer;
    box-sizing: content-box;
    width: 100%;
    aspect-ratio: 341.94/217.38;
    transition: margin 0.3s ease;
    img {
        object-fit: fill;
        width: 100%;
        height: 100%;
    }
    &:first-child {
        margin-top: 0;
    }

    @media screen and (min-width: ${breakpoints.desktop}px) {
        display: inline-block;
        width: 21rem;
    }

    ${({ animated }) => !animated && css`
        margin-right: 1rem;
        margin-bottom: 1rem;
    `}

    ${({ animated }) => animated && css`
        margin-top: -45%;
        &:hover:not(:last-child) {
            margin-bottom: 30vw;
        }

        @media screen and (min-width: ${breakpoints.desktop}px) {
            margin-right: -14rem;
            margin-top: 0;
            width: 21rem;
            &:hover:not(:last-child) {
                margin-bottom: 0;
                margin-right: -1rem;
                padding-bottom: 0;
            }
        }
    `}
`;

type Props = {
    animated?: boolean;
    giftCards: GiftCard[] | GiftCardPurchaseOption[];
    onSelectGiftCard: (giftCard: GiftCard | GiftCardPurchaseOption) => void;
} & React.HTMLAttributes<HTMLDivElement>;

const ListGiftCards: React.FC<Props> = ({
    animated = false,
    giftCards,
    onSelectGiftCard,
    ...props
}) => {
    const listRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const div = listRef.current && listRef.current.firstChild as HTMLDivElement;
        if (!div) return;
        if (!div.classList.contains("fade-in")) {
            div.classList.add("fade-in");
            return;
        }

        div.classList.remove('fade-in');
        setTimeout(() => {
            div && div.classList.add("fade-in");
        }, 1);
    }, [giftCards]);

    return <div ref={listRef}>
        <List animated={animated} {...props}>
            {giftCards.map((giftCard) => (
                <Card
                    key={giftCard.id}
                    animated={animated}
                    onClick={() => onSelectGiftCard(giftCard)}
                >
                    <img src={giftCard.image} alt={giftCard.name} />
                </Card>
            ))}
        </List>
    </div>;
};

export default ListGiftCards;
